import App from '@/App.vue'
import { axiosSetup } from '@/configs/axios'
import { PiniaColada, options as piniaColadaOptions } from '@/piniaColada'
import i18n from '@/plugins/i18n'
import vuetify from '@/plugins/vuetify'
import router from '@/router'
import * as Sentry from '@sentry/vue'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import vueDebounce from 'vue-debounce'
import VueEasyLightbox from 'vue-easy-lightbox'

axiosSetup()

const OUTDATED_BROWSER_VERSIONS = { Edg: 100, Firefox: 100, Chrome: 100, Version: 15 }

function isOutdatedBrowser(): boolean {
  // Order matters: Edge UAs also contain "Chrome", Chrome UAs also contain "Version" on some platforms.
  for (const [name, min] of Object.entries(OUTDATED_BROWSER_VERSIONS)) {
    const match = new RegExp(`${name}/(\\d+)`).exec(navigator.userAgent)
    if (match) {
      return Number(match[1]) < min
    }
  }
  return false
}

const app = createApp(App)

window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason)
})

Sentry.init({
  app,
  beforeSend(event) {
    const frames = event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? []
    const external = frames.some((f) =>
      /^(chrome|moz|edge|safari(-web)?)-extension:|^webkit-masked-url:|^safari-resource:|^app:|^about:|^file:/i.test(
        f.filename ?? ''
      )
    )
    // Set a tag if the script that throws the exception is not storefront related.
    // We do not filter out these errors to be able to rule out storefront as the source of them.
    if (external) {
      event.tags = { ...event.tags, external_script: 'true' }
    }
    // Set a tag for ancient browsers. Those user agents are primarily used by scanners / bots
    if (isOutdatedBrowser()) {
      event.tags = { ...event.tags, outdated_browser: 'true' }
    }
    return event
  },
  dsn: window.STOREFRONT_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      maskAllInputs: false,
      maskAllText: false,
      blockAllMedia: false
    })
  ],
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  normalizeDepth: 10,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: window.STOREFRONT_SENTRY_TRACES_SAMPLE_RATE ?? 1.0,
  environment: import.meta.env.MODE,
  release:
    import.meta.env.MODE == 'production'
      ? import.meta.env.PACKAGE_VERSION
      : import.meta.env.VITE_COMMIT_SHA
})

app.use(createPinia())
app.use(PiniaColada, piniaColadaOptions)
app.use(router)
app.use(vuetify)
app.use(i18n)
app.use(VueEasyLightbox)

app.directive('debounce', vueDebounce({ defaultTime: '500ms', trim: true }))

app.mount('#app')
